import React, { useId, useRef } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import thurentlogo from "../../../redesign/assets/thurent-logo.svg";
import { formatInvitationAddress, formatInvitationDate, invitationText } from "./invitationFormatting";

export const RSVPValidationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  mobile: Yup.string().matches(/^[6-9]\d{9}$/, "Enter a valid mobile number").required("Mobile number is required"),
  attendance: Yup.string().oneOf(["yes", "no"]).required("Please select your attendance"),
  remind: Yup.string().oneOf(["yes", "no"]),
  members: Yup.number().typeError("Enter a whole number").integer("Enter a whole number")
    .when("attendance", {
      is: "yes",
      then: (schema) => schema.min(1, "Must be at least 1 member").max(25, "Maximum 25 people. For more, please call the host."),
      otherwise: (schema) => schema.oneOf([0], "Count must be 0 when not attending"),
    }).required("Number of members is required"),
});

const RSVPCard = ({ eventTitle, eventType, invitedBy, startDate, venue, area, city, state, styleConfig = {}, onSubmitForm }) => {
  const id = useId();
  const submitting = useRef(false);
  const title = invitationText(eventTitle) || invitationText(eventType);
  const host = invitationText(invitedBy);
  const date = formatInvitationDate(startDate);
  const address = formatInvitationAddress({ venue, area, city, state });

  return (
    <div className="rsvp-invitation-inner" style={{ "--rsvp-text": styleConfig.textColor || "#242424", "--rsvp-heading": styleConfig.headingColor || "#222" }}>
      <div className="rsvp-glass-panel">
        <header className="rsvp-invitation-head">
          {/* <p className="rsvp-salutation">With Love &amp; Joy,</p> */}
          <p className="rsvp-introduction">You’re invited to celebrate</p>
          {title && <h1 className="rsvp-event-title">{title}</h1>}
          {date && <p className="rsvp-date">{date}</p>}
          {address && <p className="rsvp-address">{address}</p>}
          {host && <p className="rsvp-host">With love, <strong>{host}</strong></p>}
        </header>
        <div className="rsvp-panel-divider" aria-hidden="true" />
        <Formik
        initialValues={{ name: "", mobile: "", attendance: "yes", remind: "yes", members: 1, suggestion: "" }}
        validationSchema={RSVPValidationSchema}
        onSubmit={async (values, helpers) => {
          if (submitting.current) return;
          submitting.current = true;
          try { await onSubmitForm?.(values, helpers); }
          finally { submitting.current = false; helpers.setSubmitting(false); }
        }}
      >
        {({ values, touched, errors, isSubmitting, setValues, setFieldValue }) => {
          const declining = values.attendance === "no";
          const errorProps = (name) => ({
            "aria-invalid": Boolean(touched[name] && errors[name]),
            "aria-describedby": touched[name] && errors[name] ? `${id}-${name}-error` : undefined,
          });
          const fieldError = (name) => <ErrorMessage name={name} component="div" id={`${id}-${name}-error`} className="rsvp-error" role="alert" />;
          const radios = (name, label, disabled = false) => (
            <fieldset className="rsvp-question" disabled={disabled || isSubmitting} aria-describedby={disabled ? `${id}-declined` : undefined}>
              <legend>{label}</legend>
              <div className="rsvp-options">
                {["yes", "no"].map((choice) => (
                  <label className="rsvp-option" key={choice}>
                    <Field type="radio" name={name} value={choice} {...errorProps(name)}
                      onChange={() => {
                        if (name === "attendance") {
                          setValues((current) => ({ ...current, attendance: choice,
                            members: choice === "no" ? 0 : (Number.isInteger(Number(current.members)) && Number(current.members) >= 1 && Number(current.members) <= 25 ? Number(current.members) : 1),
                            remind: choice === "no" ? "no" : current.remind,
                          }));
                        } else setFieldValue(name, choice);
                      }} />
                    <span>{choice === "yes" ? "Yes" : "No"}</span>
                  </label>
                ))}
              </div>
              {fieldError(name)}
            </fieldset>
          );
          return (
            <Form className="rsvp-form-new" aria-label="RSVP" aria-busy={isSubmitting} noValidate
              onSubmitCapture={(event) => {
                if (isSubmitting) { event.preventDefault(); event.stopPropagation(); }
              }}>
              <div className="rsvp-contact-details">
                <div>
                  <label htmlFor={`${id}-name`}>Name</label>
                  <Field id={`${id}-name`} name="name" type="text" autoComplete="name" required disabled={isSubmitting} className="rsvp-input" {...errorProps("name")} />
                  {fieldError("name")}
                </div>
                <div>
                  <label htmlFor={`${id}-mobile`}>Mobile Number</label>
                  <Field id={`${id}-mobile`} name="mobile" type="tel" autoComplete="tel" inputMode="tel" required disabled={isSubmitting} className="rsvp-input" {...errorProps("mobile")} />
                  {fieldError("mobile")}
                </div>
              </div>
              <div className="rsvp-quick-actions">
                {radios("attendance", "Are you coming?")}
                <div className="rsvp-count-row">
                  <label id={`${id}-members-label`} htmlFor={`${id}-members`}>Number attending</label>
                  <div className="rsvp-stepper" id={`${id}-members`} aria-labelledby={`${id}-members-label`}>
                    <button type="button" aria-label="Decrease number attending" disabled={declining || isSubmitting || Number(values.members) <= 1}
                      onClick={() => setFieldValue("members", Math.max(1, Number(values.members) - 1))}>-</button>
                    <output className="rsvp-count" aria-label="Number of people attending" aria-valuemin="1" aria-valuemax="25" aria-valuenow={declining ? 0 : Number(values.members) || 0}>{declining ? 0 : Number(values.members) || 0}</output>
                    <button type="button" aria-label="Increase number attending" disabled={declining || isSubmitting || Number(values.members) >= 25}
                      onClick={() => setFieldValue("members", Math.min(25, Math.max(1, Number(values.members) + 1)))}>+</button>
                  </div>
                  {fieldError("members")}
                </div>
                {radios("remind", "Reminder?", declining)}
                {declining && <p id={`${id}-declined`} className="rsvp-disabled-note">Count and reminders are unavailable when not attending.</p>}
              </div>
              <div className="rsvp-submit-area">
                <button type="submit" className="rsvp-submit-button-new" disabled={isSubmitting}>{isSubmitting ? "Submitting..." : "Submit"}</button>
                <span className="rsvp-sr-only" role="status">{isSubmitting ? "Submitting your response" : ""}</span>
              </div>
            </Form>
          );
        }}
        </Formik>
      </div>
      <div className="compliment-new">Best Wishes from <Link to="https://thurent.com/"><img src={thurentlogo} alt="Thurent" /></Link></div>
    </div>
  );
};

export default RSVPCard;
